/**
 * Raw-ushort -> engineering-unit conversion for the live tick stream (item 4).
 *
 * Why the client has to do this
 * ----------------------------
 * The REST backfill returns ALREADY-CONVERTED floats (`TR` / `LR`). The SignalR
 * broadcast does not: it carries the raw ushort payload exactly as stored in
 * Cosmos (`data`). So a live tick and a polled tick describing the same moment
 * arrive in different units, and the client must close that gap — otherwise the
 * chart would mix °C from the socket with °F from the poll, or plot raw 3000-ish
 * ushorts against real temperatures, and the error would look like bad data
 * rather than a bug.
 *
 * Formulas are transcribed from DTSCommunication.MeasurementTrace (the same
 * source ProjectApiServices uses on the REST path), so both paths agree exactly:
 *
 *   TemperatureFromShort(u) = u / 10  - 273.15     -> °C
 *   LossFromShort(u)        = u / 100 - 100        -> dB
 *
 * Per-family handling, matching ProjectApiServices:
 *   TEMPERATURE   convert, then apply the imperial conversion when the project is
 *                 in imperial units (DataHelper.Unit_Temp_Imp: (c * 9) / 5 + 32)
 *   LOSS          convert; NO unit conversion — dB is unit-agnostic and the REST
 *                 path does not convert it either
 *   ZONESTORAGE   RAW PASSTHROUGH — zone values are severity codes, and the REST
 *                 path assigns `telemetryData[i].Data` unchanged
 */

/** Families the stream broadcasts, matching the server's `family` field. */
export type TelemetryFamilyName = 'TEMPERATURE' | 'ZONESTORAGE' | 'LOSS';

/** ushort -> °C. */
export function temperatureFromShort(value: number): number {
  return value / 10 - 273.15;
}

/** ushort -> dB. */
export function lossFromShort(value: number): number {
  return value / 100 - 100;
}

/** °C -> °F, matching DataHelper.Unit_Temp_Imp. */
export function toImperial(celsius: number): number {
  return (celsius * 9) / 5 + 32;
}

/**
 * Convert one raw row for a family.
 *
 * @param data     raw ushort samples as broadcast
 * @param family   which family the row belongs to
 * @param isMetric project unit preference; only affects TEMPERATURE
 */
export function convertRawRow(
  data: readonly number[] | null | undefined,
  family: TelemetryFamilyName,
  isMetric: boolean
): number[] {
  if (!data || data.length === 0) {
    return [];
  }
  const out = new Array<number>(data.length);

  switch (family) {
    case 'TEMPERATURE':
      for (let i = 0; i < data.length; i++) {
        const celsius = temperatureFromShort(data[i]);
        out[i] = isMetric ? celsius : toImperial(celsius);
      }
      return out;

    case 'LOSS':
      for (let i = 0; i < data.length; i++) {
        out[i] = lossFromShort(data[i]);
      }
      return out;

    case 'ZONESTORAGE':
      // Severity codes — the REST path passes these through untouched.
      for (let i = 0; i < data.length; i++) {
        out[i] = data[i];
      }
      return out;

    default:
      return [...data];
  }
}

/** True when the string names a family this client understands. */
export function isKnownFamily(family: string | undefined): family is TelemetryFamilyName {
  return family === 'TEMPERATURE' || family === 'ZONESTORAGE' || family === 'LOSS';
}

/** Map a broadcast family onto the per-family poll cursor name. */
export function cursorFamilyFor(
  family: TelemetryFamilyName
): 'temperature' | 'zone' | 'loss' {
  switch (family) {
    case 'TEMPERATURE':
      return 'temperature';
    case 'ZONESTORAGE':
      return 'zone';
    case 'LOSS':
      return 'loss';
  }
}

/**
 * Absolute zero, in each encoding the payload may arrive in.
 *
 * A "no reading" sample is written as ABSOLUTE ZERO IN ITS OWN UNITS — the raw
 * ushort 0 decodes through `u/10 - 273.15` to -273.15 C, and the server's
 * imperial conversion turns that into -459.67 F. Three different numbers, one
 * physical fact.
 */
export const ABSOLUTE_ZERO_C = -273.15;
export const ABSOLUTE_ZERO_F = -459.67;

/**
 * Is this sample a "no reading" sentinel rather than a temperature?
 *
 * ONE THRESHOLD SERVES BOTH ENCODINGS, deliberately. -273.15 C and -459.67 F are
 * both far below -250, and no cure produces a reading below -250 in either unit —
 * the coldest thing either scale describes above that is still hundreds of degrees
 * from anything a liner sees. So this does not need to know which encoding the
 * payload carries, which matters because the caller often does not either.
 *
 * WHY IT EXISTS. Measured on beta-dev, S3632 REV_3: 25 of 309 points in the
 * /graph marker series were exactly -273.15, plotted as though they were
 * temperatures. The axis floor followed them to absolute zero and squashed a
 * 55-66 C trace into the top few percent of the chart — which reads as a broken
 * chart rather than as missing data.
 *
 * OMIT, NEVER CLAMP. A gap in a line is honest; a plotted absolute zero is a false
 * reading and a clamped one is a fabricated reading. Both are worse than a gap.
 */
export function isNoReading(value: number | null | undefined): boolean {
  return value == null || !Number.isFinite(value) || value <= -250;
}

/**
 * The newest tick that actually MEASURED something.
 *
 * "Land on the end of the cure" was the approximation; this is what it meant.
 * The last rows of a finished cure are all -273.15 C — raw ushort zero, what the
 * DTS writes once it stops measuring — so the literal newest tick is a frame with
 * no current-temperature reading at all. A chart there draws its running-max
 * trace and nothing else, which reads as a chart missing a line rather than as a
 * cure that has ended. S3640 has four such rows.
 *
 * Scans BACK from the newest and stops at the first row with any reading on it.
 * On a normal cure that is 0-4 steps.
 *
 * ANY reading ON THE FIBRE counts, not just inside the liner window. The DTS
 * writes the whole row or none of it, so "this frame measured something" is a
 * property of the row; requiring a reading inside the liner would also reject a
 * genuine frame recorded before the liner was in position, and would need
 * geometry this helper should not have to know about.
 *
 * BOUNDED, and it falls back to the newest. A project whose rows are ALL blank
 * is a real possibility — a cure that never started, a misconfigured device —
 * and walking every retained tick to discover that, on every backfill, is a cost
 * with no benefit. After `maxScan` steps it gives up and returns the newest,
 * which is exactly the behaviour this replaces.
 */
export function newestReadableIndex(
  firstIndex: number,
  nextIndex: number,
  rowAt: (index: number) => { TR?: ArrayLike<number> | null } | null | undefined,
  maxScan = 64
): number {
  // NEVER NEGATIVE, and never NaN. An empty buffer gives newest = -1, and a
  // caller whose buffer does not report firstIndex would otherwise make every
  // comparison below NaN — which is false, so the guard would fall through and
  // this would hand back -1 as a tick. tick-follow.spec.ts names that invariant
  // ("never returns a negative tick when no rows have arrived") and caught this.
  const first = Number.isFinite(firstIndex) ? Math.max(0, firstIndex) : 0;
  const newest = nextIndex - 1;
  if (!Number.isFinite(newest) || newest < first) {
    return 0;
  }
  const floor = Math.max(first, newest - maxScan + 1);
  for (let i = newest; i >= floor; i--) {
    const tr = rowAt(i)?.TR;
    if (!tr) {
      continue;
    }
    for (let k = 0; k < tr.length; k++) {
      if (!isNoReading(tr[k])) {
        return i;
      }
    }
  }
  return newest;
}

/**
 * Metres per foot. Exact by definition, and exactly what the server uses — measured
 * on beta-dev, S3632 REV_3, two requests differing only in unitCode:
 * SI 0.150000006 (unitCode=0) -> 0.492126018 (unitCode=1), and 0.150000006/0.3048
 * = 0.49212601..., so the server's own factor is this one to eight figures.
 */
export const METRES_PER_FOOT = 0.3048;

/**
 * MARKER POSITIONS ARE THE ONE DISTANCE THE SERVER DOES NOT CONVERT.
 *
 * The rule everywhere else in this app is "the server converts, the client displays"
 * — the client sends unitCode and every distance comes back in the requested system.
 * Measured on beta-dev, S3632 REV_3, the same request at unitCode=0 and unitCode=1:
 *
 *     CRB      1382 -> 4534    (x3.28)
 *     CRE      1419 -> 4655    (x3.28)
 *     span       37 ->  121    (x3.2703)
 *     SI  0.150000006 -> 0.492126018  (x3.2808)
 *     MAR[0].P   10 ->   10    (x1.0000)   <-- not converted
 *
 * MAR rides the same payload as CRB/CRE and comes back byte-identical in both unit
 * systems. So MarkerInfo.P is ALWAYS METRES, in every response, on every host. The
 * desktop stores 10 and means 10 metres; the server hands that number straight
 * through. That is the contract, not a bug to fix server-side before Thursday.
 *
 * THE CONSEQUENCE, AND WHY THIS IS NOT A LABEL FIX. Four call sites read mar.P and
 * all four treated it as though it were already in axis units:
 *   - the marker's dataSeriesName suffix (read 10.0ft when it means 32.8ft),
 *   - percAtMarker = pos / (CRE - CRB), which picks WHICH READING the /graph left
 *     chart plots — 10/37 = 0.270 metric vs 10/121 = 0.083 imperial, so the same
 *     marker sampled two different physical points on the liner,
 *   - x1 on the VerticalLineAnnotation in graph, temperature and curing, so the
 *     line was drawn at 10ft on an axis running to 121ft,
 *   - and the drag write-back, which read annt.x1 in AXIS units and POSTed it into
 *     P, silently writing a foot number into a metre field on every imperial drag.
 *
 * DO NOT "FIX" THE DISTANCE AXIS. The axis converts correctly already — CRB, CRE and
 * SI are all converted server-side, as measured above. Converting it client-side too
 * would double-convert by 3.28x. Only P needs converting, and only at these edges.
 */
export function metresToDisplay(metres: number, isMetric: boolean): number {
  return isMetric ? metres : metres / METRES_PER_FOOT;
}

/**
 * Inverse of metresToDisplay, for the drag write-back. SciChart hands back x1 in
 * whatever units the axis is in; MarkerInfo.P must be metres before it is persisted.
 */
export function displayToMetres(display: number, isMetric: boolean): number {
  return isMetric ? display : display * METRES_PER_FOOT;
}
